import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import InfiniteMenu from '../../components/InfiniteMenu/InfiniteMenu';
import Modal from '../../components/common/Modal';
import Button from '../../components/common/Button';
import { getShop } from '../../services/marketplaceService';
import { resolveImageUrl, onImageError } from '../../utils/imageUrl';

function formatPrice(p) {
  if (p == null) return '';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(p);
}

function ArtDetailModal({ art, onClose, onNavigate }) {
  if (!art) return null;

  return (
    <Modal
      isOpen={Boolean(art)}
      onClose={onClose}
      title={art.title || 'Artwork Details'}
      subtitle={art.artist_name ? `By ${art.artist_name}` : 'Artist Collection'}
      maxWidth={480}
      actions={
        <div style={{ display: 'flex', gap: '0.75rem', width: '100%' }}>
          <Button
            variant="primary"
            onClick={() => onNavigate(`/marketplace/art/${art.art_id}`)}
            style={{ flex: 1 }}
          >
            View Details →
          </Button>
          <Button
            variant="secondary"
            onClick={() => onNavigate(`/marketplace/checkout/${art.art_id}?type=sale`)}
            style={{ flex: 1 }}
          >
            🛒 Buy Now
          </Button>
        </div>
      }
    >
      <div className="artist-shop-modal-content">
        <div
          style={{
            position: 'relative',
            width: '100%',
            height: 260,
            borderRadius: '16px',
            overflow: 'hidden',
            marginBottom: '1rem',
            border: '1px solid rgba(255,255,255,0.08)',
          }}
        >
          <img
            src={resolveImageUrl(art.image_url)}
            alt={art.title || 'Artwork'}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            onError={onImageError}
          />
          <span
            style={{
              position: 'absolute',
              bottom: 12,
              left: 12,
              fontSize: '0.75rem',
              padding: '0.25rem 0.75rem',
              borderRadius: '999px',
              background: 'rgba(16, 185, 129, 0.2)',
              backdropFilter: 'blur(8px)',
              border: '1px solid rgba(16, 185, 129, 0.4)',
              color: '#10b981',
              fontWeight: 700,
              letterSpacing: '0.04em',
            }}
          >
            🏷️ For Sale
          </span>
        </div>

        {art.description && (
          <p
            style={{
              color: 'var(--text-secondary, #94a3b8)',
              fontSize: '0.9rem',
              lineHeight: 1.6,
              margin: '0.5rem 0 1rem',
            }}
          >
            {art.description.slice(0, 200)}
            {art.description.length > 200 ? '…' : ''}
          </p>
        )}

        {art.price != null && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '0.85rem 1rem',
              background: 'rgba(255,255,255,0.03)',
              borderRadius: '12px',
              border: '1px solid rgba(255,255,255,0.06)',
              marginBottom: '0.5rem',
            }}
          >
            <span
              style={{
                fontSize: '0.75rem',
                color: '#64748b',
                textTransform: 'uppercase',
                fontWeight: 700,
                letterSpacing: '0.08em',
              }}
            >
              Price
            </span>
            <span
              style={{
                fontFamily: 'var(--font-mono, "JetBrains Mono", monospace)',
                fontSize: '1.25rem',
                fontWeight: 800,
                color: '#10b981',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {formatPrice(art.price)}
            </span>
          </div>
        )}
      </div>
    </Modal>
  );
}

function transformToMenuItems(arts) {
  if (!Array.isArray(arts) || arts.length === 0) return [];
  return arts.map(art => ({
    image: resolveImageUrl(art.image_url),
    link: '#',
    title: art.title || 'Untitled',
    description: art.status === 'for_selling' ? 'For Sale' : 'For Rent',
    artData: art,
  }));
}

export default function ArtistShop() {
  const { artistId } = useParams();
  const navigate = useNavigate();
  const [shop, setShop] = useState(null);
  const [listings, setListings] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalArt, setModalArt] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const fetchData = async () => {
      try {
        const res = await getShop(artistId);
        if (!cancelled) {
          const shopData = res?.data?.data?.shop || null;
          const listingsData = res?.data?.data?.listings || [];
          setShop(shopData);
          setListings(listingsData);
          setMenuItems(transformToMenuItems(listingsData));
        }
      } catch {
        if (!cancelled) setError('Shop not found.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, [artistId]);

  const handleItemClick = (item) => {
    if (item?.artData) setModalArt(item.artData);
  };

  const handleNavigate = (path) => {
    setModalArt(null);
    navigate(path);
  };

  if (loading) return <div style={{ textAlign: 'center', padding: '4rem', color: '#888' }}>Loading...</div>;
  if (error || !shop) return <div style={{ textAlign: 'center', padding: '4rem', color: '#ef4444' }}>{error || 'Shop not found'}</div>;

  const initial = (shop.shop_name || shop.artist_name || 'A')[0].toUpperCase();

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto' }}>
      {/* Breadcrumb */}
      <nav style={{ padding: '1rem 1rem 0', fontSize: '0.85rem', color: '#888' }}>
        <Link to="/marketplace" style={{ color: '#7c3aed', textDecoration: 'none' }}>Marketplace</Link>
        <span style={{ margin: '0 0.5rem' }}>›</span>
        <span style={{ color: '#ccc' }}>{shop.shop_name || 'Artist Shop'}</span>
      </nav>

      {/* Banner */}
      <div style={{
        height: 200,
        background: shop.banner_url ? `url(${resolveImageUrl(shop.banner_url)}) center/cover` : 'linear-gradient(135deg, #7c3aed, #2563eb)',
        borderRadius: '0 0 1.5rem 1.5rem',
        marginTop: '0.5rem',
      }} />

      {/* Shop Info */}
      <div style={{ padding: '0 1.5rem', textAlign: 'center' }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '-32px' }}>
          {shop.avatar_url ? (
            <img
              src={resolveImageUrl(shop.avatar_url)}
              alt={shop.artist_name || ''}
              style={{
                width: 64, height: 64, borderRadius: '50%', border: '3px solid #0d0716',
                objectFit: 'cover',
              }}
              onError={(e) => { e.target.style.display = 'none'; if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex'; }}
            />
          ) : null}
          <div style={{
            width: 64, height: 64, borderRadius: '50%', border: '3px solid #0d0716',
            background: 'linear-gradient(135deg, #7c3aed, #2563eb)',
            display: shop.avatar_url ? 'none' : 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontWeight: 700, fontSize: '1.5rem',
          }}>
            {initial}
          </div>
        </div>
        <h1 style={{ fontSize: '2rem', fontWeight: 700, margin: '0.75rem 0 0' }}>{shop.shop_name || 'Artist Shop'}</h1>
        {shop.artist_name && (
          <p style={{ color: '#7c3aed', fontSize: '0.9rem', margin: '0.25rem 0' }}>by {shop.artist_name}</p>
        )}
        {shop.bio && <p style={{ color: '#aaa', marginTop: '0.5rem', maxWidth: 600, margin: '0.5rem auto 0' }}>{shop.bio}</p>}
        <p style={{ color: '#666', fontSize: '0.85rem', marginTop: '0.5rem' }}>
          {listings.length} artwork{listings.length !== 1 ? 's' : ''} listed
        </p>
        {shop.policies && (
          <details style={{ marginTop: '0.75rem', color: '#888', fontSize: '0.85rem' }}>
            <summary style={{ cursor: 'pointer' }}>Shop Policies</summary>
            <p style={{ marginTop: '0.5rem', whiteSpace: 'pre-wrap' }}>{shop.policies}</p>
          </details>
        )}
      </div>

      {/* 3D Gallery — InfiniteMenu */}
      <div style={{ padding: '1.5rem 0' }}>
        <h2 style={{ fontSize: '1.3rem', marginBottom: '1rem', paddingLeft: '1rem' }}>
          Available Works
        </h2>
        {menuItems.length === 0 ? (
          <p style={{ color: '#888', padding: '2rem', textAlign: 'center' }}>No artworks listed yet.</p>
        ) : (
          <div style={{ height: 500, position: 'relative' }}>
            <InfiniteMenu
              items={menuItems}
              scale={1}
              onItemClick={handleItemClick}
            />
          </div>
        )}
      </div>

      {/* Art Detail Modal */}
      {modalArt && (
        <ArtDetailModal
          art={modalArt}
          onClose={() => setModalArt(null)}
          onNavigate={handleNavigate}
        />
      )}
    </div>
  );
}
